import React from 'react';
import { motion } from 'framer-motion';

/**
 * AuthLayout
 * 
 * The foundation for all authentication and landing pages.
 * Features:
 * - Animated mesh gradient background (Light theme)
 * - Floating background elements for depth
 * - Centered content container
 */
const AuthLayout = ({ children, title, subtitle }) => {
    return (
        <div className="min-h-screen relative overflow-hidden bg-gray-50 flex items-center justify-center p-4">
            {/* Animated Background Gradients */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {/* Top Left Blob */}
                <motion.div
                    animate={{
                        scale: [1, 1.2, 1],
                        x: [0, 50, 0],
                        y: [0, 30, 0]
                    }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                    className="absolute -top-40 -left-40 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-70"
                />

                {/* Top Right Blob */}
                <motion.div
                    animate={{
                        scale: [1, 1.1, 1],
                        x: [0, -30, 0],
                        y: [0, 50, 0]
                    }}
                    transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    className="absolute -top-20 -right-20 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-70"
                />

                {/* Bottom Left Blob */}
                <motion.div
                    animate={{
                        scale: [1, 1.3, 1],
                        x: [0, 60, 0],
                        y: [0, -40, 0]
                    }}
                    transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                    className="absolute -bottom-40 -left-40 w-96 h-96 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-70"
                />

                {/* Bottom Right Blob */}
                <motion.div
                    animate={{
                        scale: [1, 1.2, 1],
                        x: [0, -40, 0],
                        y: [0, -60, 0]
                    }}
                    transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 3 }}
                    className="absolute -bottom-40 -right-40 w-96 h-96 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-70"
                />
            </div>

            {/* Content Container */}
            <div className="relative z-10 w-full max-w-6xl mx-auto">
                {title && (
                    <div className="text-center mb-8">
                        <motion.h1
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-3"
                        >
                            {title}
                        </motion.h1>
                        {subtitle && (
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="text-gray-600 text-lg md:text-xl"
                            >
                                {subtitle}
                            </motion.p>
                        )}
                    </div>
                )}

                {children}
            </div>
        </div>
    );
};

export default AuthLayout;
